/**
 * UnifiedCardSearch Component
 *
 * @deprecated Scheduled for removal once all callers use the shared catalog/inventory search stack
 * (`CardsSearchResults`, inventory flows). Prefer evolving those surfaces rather than extending this module.
 *
 * Consolidated search component that replaces all separate search implementations.
 * Features:
 * - Searches across all cards in the database OR specific inventory
 * - Game filter dropdown (loads all games)
 * - Set filter dropdown (loads sets for selected game)
 * - Rich card results with images
 * - Supports both catalog search (all cards) and inventory search (store products)
 */

import { SetImage } from '../ui/SetImage'
import { formatSetCode } from '../../utils/setCode'
import { hasCardNumber } from '../../utils/cardNumber'
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { X, Loader2, AlertTriangle, Package, Flag } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../utils/api'
import { useCurrency, isSupportedCurrency, type Currency } from '../../contexts/CurrencyContext'
import { canSearchQuery, normalizeSearchText, scoreSearchRelevance } from '../../utils/searchMatching'
import { normalizePrintingLabel } from '../../utils/variantLabeling'
import { CardImageModal } from '../modals/CardImageModal'
import { ReportIssueModal, ReportSetIssueModal, type ReportIssueContext, type ReportSetIssueContext } from '../modals'
import { SearchInput } from '../ui/SearchInput'
import { SET_TYPE_OPTIONS, type SearchGame, type SearchSet, type SetType } from './UnifiedCardSearchFilters'
import { FilterBar, type FilterField } from '../search/FilterBar'
import styles from './GlobalCardSearch.module.css'
import CardName from './CardName'

/**
 * One letter matched thousands of cards and cost the server a scan per keystroke; a single
 * letter is never what someone is looking for. Pure numbers ("4") still search — see
 * canSearchQuery.
 */
const MIN_QUERY_LENGTH = 2

export interface CardSearchResult {
  id: string
  name: string
  setNumber: string
  rarity?: string
  imageUrl: string | null
  setId: string
  setName: string
  setCode: string
  setType?: 'set' | 'promo' | 'deck' | 'collection' | 'energy' | 'vending' | 'other'
  setReleaseDate?: string
  setSymbolUrl?: string | null
  setLogoUrl?: string | null
  series?: string
  language?: string
  gameId: string
  gameName: string
  gameSlug?: string
  priceCents: number
  priceUnavailable?: boolean
  printing?: string
  condition?: string
  variants: Array<{
    id: string
    printing: string | null
    condition: string | null
    price: number
    priceCents: number
  }>
  metadata?: Record<string, any>
  inventoryVariantCount?: number
}

export interface InventorySearchResult {
  id: string
  cardId: string
  cardName: string
  setNumber: string
  rarity?: string
  imageUrl: string | null
  setId: string
  setName: string
  gameName: string
  printing: string
  condition: string
  quantity: number
  /**
   * The engine's listed price in USD cents (sale, floor, manual expiry and markup applied) — what
   * a POS sale line records. 0 with `priceUnavailable` when it cannot be priced right now.
   */
  priceCents: number
  /** The same listing in the store's currency, exactly as the inventory screen shows it. */
  storePriceCents?: number | null
  storeCurrency?: string
  /** The card's market price in the store's currency at the same rate; null when none / not a card. */
  marketStoreCents?: number | null
  priceUnavailable?: boolean
}

const normalizeVariantToken = (value?: string | null): string => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

const getConditionClassName = (condition?: string | null): string => {
  const normalized = normalizeVariantToken(condition)

  if (!normalized) return 'conditionNeutral'
  if (normalized === 'sealed' || normalized.includes('factory sealed')) return 'conditionSealed'
  if (normalized === 'damaged' || normalized === 'dmg' || normalized.includes('poor')) return 'conditionDamaged'
  if (normalized === 'hp' || normalized.includes('heavily played') || normalized.includes('heavy play')) return 'conditionHeavilyPlayed'
  if (normalized === 'mp' || normalized.includes('moderately played') || normalized.includes('moderate play')) return 'conditionModeratelyPlayed'
  if (normalized === 'lp' || normalized.includes('lightly played') || normalized.includes('light play') || normalized === 'excellent') return 'conditionLightlyPlayed'
  if (normalized === 'nm' || normalized === 'mint' || normalized === 'm' || normalized.includes('near mint') || normalized.includes('gem mint')) return 'conditionNearMint'

  return 'conditionNeutral'
}

const getPrintingClassName = (printing?: string | null): string => {
  const normalized = normalizeVariantToken(normalizePrintingLabel(printing))

  if (!normalized) return 'printingNeutral'
  if (normalized.includes('reverse holo') || normalized.includes('holo reverse')) return 'printingReverseHolofoil'
  if (normalized.includes('non holo') || normalized.includes('non-holo') || normalized === 'normal' || normalized.includes('standard')) return 'printingNormal'
  if (normalized.includes('etched')) return 'printingEtched'
  if (normalized.includes('1st edition') || normalized.includes('1st ed') || normalized.includes('first edition')) return 'printingFirstEdition'
  if (normalized.includes('unlimited')) return 'printingUnlimited'
  if (normalized.includes('holofoil') || normalized.includes('holo') || normalized.includes('foil')) return 'printingHolofoil'

  return 'printingNeutral'
}

const getGameAccentClassName = (gameName?: string | null): string => {
  const normalized = normalizeVariantToken(gameName)

  if (!normalized) return 'gameAccentDefault'
  if (normalized.includes('pokemon') && (normalized.includes('japan') || normalized.includes('japanese'))) return 'gameAccentPokemonJapan'
  if (normalized.includes('pokemon')) return 'gameAccentPokemon'
  if (normalized.includes('yu gi oh') || normalized.includes('yugioh')) return 'gameAccentYugioh'
  if (normalized.includes('magic') || normalized.includes('mtg') || normalized.includes('commander')) return 'gameAccentMagic'
  if (normalized.includes('one piece')) return 'gameAccentOnePiece'
  if (normalized.includes('lorcana') || normalized.includes('disney')) return 'gameAccentLorcana'
  if (normalized.includes('riftbound') || normalized.includes('league of legends')) return 'gameAccentRiftbound'

  return 'gameAccentDefault'
}

function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (Math.imul(31, hash) + str.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

function getSetAccentStyle(setCode?: string | null): React.CSSProperties {
  if (!setCode) return {}
  const hash = hashString(setCode.trim().toUpperCase())
  const rawHue = hash % 360
  const hue = rawHue < 25 ? rawHue + 42 : rawHue > 330 ? rawHue - 48 : rawHue
  const sat = 58 + ((hash >> 10) % 20) // 58–78 %
  const lightBorder = 32 + ((hash >> 16) % 14) // 32–45 %
  const darkBorder = 70 + ((hash >> 18) % 12) // 70–81 %
  return {
    '--set-accent-bg-light': `hsla(${hue}, ${sat}%, 52%, 0.14)`,
    '--set-accent-border-light': `hsla(${hue}, ${sat}%, ${lightBorder}%, 0.42)`,
    '--set-accent-text-light': `hsl(${hue}, ${Math.max(62, sat)}%, 26%)`,
    '--set-accent-bg-dark': `hsla(${hue}, ${Math.max(64, sat)}%, 60%, 0.24)`,
    '--set-accent-border-dark': `hsla(${hue}, ${Math.max(64, sat)}%, ${darkBorder}%, 0.52)`,
    '--set-accent-text-dark': `hsl(${hue}, ${Math.max(70, sat)}%, 84%)`,
  } as React.CSSProperties
}

function toLowResImageUrl(imageUrl: string | null): string | null {
  if (!imageUrl) return null
  if (imageUrl.includes('/cards/webp/')) {
    return imageUrl.replace('/cards/webp/', '/cards/thumbs/')
  }
  return imageUrl
}

interface UnifiedCardSearchProps {
  onSelectCard: (card: CardSearchResult | InventorySearchResult) => void
  placeholder?: string
  autoFocus?: boolean
  className?: string
  searchType?: 'catalog' | 'inventory' // catalog = all cards, inventory = store products
  showGameFilter?: boolean
  showSetFilter?: boolean
  defaultGameId?: string
  defaultSetId?: string
  variant?: 'default' | 'setsLike'
  showResultTypeFilter?: boolean
  defaultSearchBy?: 'both' | 'cards' | 'sets'
  allowSetResults?: boolean
  showSetTypeOptions?: boolean
  resultsSource?: 'default' | 'trade'
  resultsTradeId?: string
  resultsTradeSide?: 'store' | 'customer'
  clearQueryOnSelect?: boolean
  manualAddLabel?: string
  onManualAdd?: (query: string) => void
  catalogVariantPreview?: 'interactive' | 'count'
  /** Above the filter chips, e.g. the header search's All cards / My inventory switch. */
  panelHeader?: React.ReactNode
  /** Results and filters fill the parent instead of dropping down, for the phone's full-screen search. */
  inline?: boolean
  /** Show the dedicated set-number / set-code box beside the name box. */
  showSetNumberInput?: boolean
  setNumberPlaceholder?: string
}

export const UnifiedCardSearch: React.FC<UnifiedCardSearchProps> = ({
  onSelectCard,
  placeholder = 'Search cards by name...',
  autoFocus = false,
  className = '',
  searchType = 'catalog',
  showGameFilter = true,
  showSetFilter = false,
  defaultGameId,
  defaultSetId,
  variant = 'default',
  showResultTypeFilter = false,
  defaultSearchBy = 'cards',
  allowSetResults = true,
  showSetTypeOptions = true,
  resultsSource = 'default',
  resultsTradeId,
  resultsTradeSide,
  clearQueryOnSelect = true,
  manualAddLabel = 'Can’t find a card? Add manually',
  onManualAdd,
  catalogVariantPreview = 'interactive',
  panelHeader,
  inline = false,
  showSetNumberInput = true,
  setNumberPlaceholder = 'Set no. / code',
}) => {
  const navigate = useNavigate()
  const { formatPrice } = useCurrency()
  const [query, setQuery] = useState('')
  const [setNumberQuery, setSetNumberQuery] = useState('')
  const [results, setResults] = useState<(CardSearchResult | InventorySearchResult)[]>([])
  const [loading, setLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [games, setGames] = useState<SearchGame[]>([])
  const [sets, setSets] = useState<SearchSet[]>([])
  const [selectedGameId, setSelectedGameId] = useState(defaultGameId || '')
  const [selectedSetId, setSelectedSetId] = useState(defaultSetId || '')
  const [loadingSets, setLoadingSets] = useState(false)
  const [total, setTotal] = useState(0)
  /** True when the backend fell back to fuzzy (typo/punctuation-tolerant) matching. */
  const [isFuzzy, setIsFuzzy] = useState(false)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [reportContext, setReportContext] = useState<ReportIssueContext | null>(null)
  const [reportSetModalOpen, setReportSetModalOpen] = useState(false)
  const [reportSetContext, setReportSetContext] = useState<ReportSetIssueContext | null>(null)
  const [selectedPrintingByCard, setSelectedPrintingByCard] = useState<Record<string, string>>({})
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null)
  const [zoomedImageAlt, setZoomedImageAlt] = useState<string>('')
  const isZoomedRef = useRef(false)
  const initialSearchBy: 'cards' | 'sets' | 'both' = defaultSearchBy === 'sets' || defaultSearchBy === 'both' ? defaultSearchBy : 'cards'
  const [searchBy, setSearchBy] = useState<'cards' | 'sets' | 'both'>(initialSearchBy)
  const [selectedSetTypes, setSelectedSetTypes] = useState<SetType[]>([])
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [releaseDateMin, setReleaseDateMin] = useState('')
  const [releaseDateMax, setReleaseDateMax] = useState('')

  const hasFilters = showGameFilter || showSetFilter || (searchType === 'catalog' && showSetTypeOptions)

  // Set search results shown in dropdown
  interface SetSearchResult {
    id: string
    setName: string
    setCode: string
    setType?: SetType
    cardCount: number
    gameName: string
    gameId?: string
    gameSlug?: string
    releaseDate?: string
    printedTotal?: number | null
    series?: string | null
    language?: string
    symbolUrl?: string | null
    logoUrl?: string | null
    coverImageUrl?: string | null
  }
  const [setSearchResults, setSetSearchResults] = useState<SetSearchResult[]>([])
  
  /** Either box counts as "the user is searching" — a bare set number is a valid search. */
  const hasSearchInput = useMemo(
    () => canSearchQuery(normalizeSearchText(query), MIN_QUERY_LENGTH) || Boolean(normalizeSearchText(setNumberQuery)),
    [query, setNumberQuery],
  )

  const debouncedQuery = useDebouncedValue(query, 300)
  const debouncedSetNumberQuery = useDebouncedValue(setNumberQuery, 300)
  const debouncedQueryFiredRef = useRef(false)
  const searchRequestController = useRef<AbortController | null>(null)
  /** The search the results on screen answer, and the one in flight (see searchCards). */
  const shownSearchKeyRef = useRef<string | null>(null)
  const inFlightSearchKeyRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  /** Tracks which gameId the current `sets` state was loaded for. */
  const setsLoadedForGameRef = useRef<string>('')

  // Load games on mount
  useEffect(() => {
    if (showGameFilter) {
      loadGames()
    }
  }, [showGameFilter])

  // Load sets when game changes
  useEffect(() => {
    if (showSetFilter && selectedGameId) {
      loadSets(selectedGameId)
    } else {
      setSets([])
      setSelectedSetId('')
    }
  }, [selectedGameId, showSetFilter])

  const filteredSets = useMemo(() => {
    if (!Array.isArray(sets)) return []
    const sorted = [...sets].sort((a, b) => {
      const aDate = a.releaseDate ? new Date(a.releaseDate).getTime() : 0
      const bDate = b.releaseDate ? new Date(b.releaseDate).getTime() : 0
      if (aDate !== bDate) return bDate - aDate
      return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    })
    return sorted
  }, [sets])

  const filteredGames = useMemo(() => {
    if (!Array.isArray(games)) return []
    // Prefer only games that have at least one "usable" set.
    // /games returns recent sets including hasCached/lastSyncedAt.
    const usableGames = games.filter((g: any) => {
      const recentSets = Array.isArray(g?.sets) ? g.sets : []
      return recentSets.some((s: any) => Boolean(s?.hasCached) || Boolean(s?.lastSyncedAt))
    })

    return usableGames.length > 0 ? usableGames : games
  }, [games])

  useEffect(() => {
    if (!selectedSetId) return
    if (setsLoadedForGameRef.current !== selectedGameId) return // sets still loading
    if (filteredSets.some((s: any) => s.id === selectedSetId)) return
    setSelectedSetId('')
  }, [filteredSets, selectedSetId, selectedGameId])

  // Focus input on mount if autoFocus
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [autoFocus])

  // Keep isZoomedRef in sync so the click-outside handler can read it without stale closure
  useEffect(() => {
    isZoomedRef.current = zoomedImageUrl !== null
  }, [zoomedImageUrl])

  // Click outside to close — skipped while the image zoom modal is open (it renders via portal)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isZoomedRef.current) return
      // The phone filter sheet lives on <body>, outside the container; taps in it are not "outside".
      if ((e.target as Element | null)?.closest?.('[data-bottom-sheet], [data-dismiss-keep]')) return
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadGames = async () => {
    try {
      const response = await api.get('/games')
      const data = response.data.data || {}
      const gameData = data.games || []
      setGames(Array.isArray(gameData) ? gameData : [])
    } catch (err) {
      console.error('Error loading games:', err)
      setGames([])
    }
  }

  const loadSets = async (gameId: string) => {
    if (!gameId) {
      setsLoadedForGameRef.current = ''
      setSets([])
      return
    }

    setsLoadedForGameRef.current = ''
    setSets([])
    setLoadingSets(true)
    try {
      const response = await api.get(`/games/${gameId}/sets`)
      const data = response.data.data || {}
      const setData = data.sets || []
      const normalized = Array.isArray(setData)
        ? setData.map((s: any) => ({
          id: s.id,
          name: s.name ?? s.setName ?? '',
          code: s.code ?? s.setCode ?? '',
          setType: s.setType,
          releaseDate: s.releaseDate,
          language: s.language
        }))
        : []
      setSets(normalized)
      setsLoadedForGameRef.current = gameId
    } catch (err) {
      console.error('Error loading sets:', err)
      setSets([])
      setsLoadedForGameRef.current = gameId
    } finally {
      setLoadingSets(false)
    }
  }

  const searchCards = useCallback(async (
    searchQuery: string,
    overrideSetNumberQuery?: string,
    options: { skipIfCurrent?: boolean } = {},
  ) => {
    const normalizedQuery = normalizeSearchText(searchQuery)
    const normalizedSetNumber = normalizeSearchText(overrideSetNumberQuery ?? setNumberQuery)

    // Allow empty query for inventory search to auto-load results, and for a
    // set-number-only search ("0044" with no name) in either mode.
    if (searchType === 'catalog' && !canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH) && !normalizedSetNumber) {
      setResults([])
      setTotal(0)
      setSetSearchResults([])
      shownSearchKeyRef.current = null
      return
    }

    const searchKey = JSON.stringify([
      searchType, searchBy, allowSetResults, normalizedQuery, normalizedSetNumber, selectedGameId, selectedSetId,
      selectedSetTypes, priceMin, priceMax, releaseDateMin, releaseDateMax,
    ])
    // Re-focusing the box used to fire the same search again at once, undebounced: the
    // logs showed it as back-to-back duplicate requests. Nothing has changed, so neither
    // would the answer.
    if (options.skipIfCurrent && (searchKey === shownSearchKeyRef.current || searchKey === inFlightSearchKeyRef.current)) {
      return
    }

    setLoading(true)

    if (searchRequestController.current) {
      searchRequestController.current.abort()
    }
    const controller = new AbortController()
    searchRequestController.current = controller
    inFlightSearchKeyRef.current = searchKey

    try {
      const params: Record<string, string> = {
        q: normalizedQuery,
        limit: '30'
      }

      if (normalizedSetNumber) {
        params.number = normalizedSetNumber
      }

      if (selectedGameId) {
        params.gameId = selectedGameId
      }

      if (selectedSetId) {
        params.setId = selectedSetId
      }

      if (selectedSetTypes.length > 0) {
        params.setTypes = selectedSetTypes.join(',')
      }

      if (priceMin) {
        params.priceMin = priceMin
      }

      if (priceMax) {
        params.priceMax = priceMax
      }

      if (releaseDateMin) {
        params.releaseDateMin = releaseDateMin
      }

      if (releaseDateMax) {
        params.releaseDateMax = releaseDateMax
      }

      const shouldSearchCards = searchType === 'inventory' || searchBy !== 'sets'
      const shouldSearchSets =
        searchType === 'catalog' &&
        !selectedSetId &&
        canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH) &&
        allowSetResults &&
        searchBy !== 'cards'

      const endpoint = searchType === 'inventory' ? '/inventory/search' : '/cards/search'

      const cardPromise = shouldSearchCards
        ? api.get(endpoint, { params, signal: controller.signal })
        : Promise.resolve(null)
      const setsPromise = shouldSearchSets
        ? api
            .get('/sets/search', {
              params: selectedGameId
                ? {
                    q: normalizedQuery,
                    limit: '5',
                    gameId: selectedGameId,
                    ...(selectedSetTypes.length > 0 ? { setTypes: selectedSetTypes.join(',') } : {}),
                  }
                : {
                    q: normalizedQuery,
                    limit: '5',
                    ...(selectedSetTypes.length > 0 ? { setTypes: selectedSetTypes.join(',') } : {}),
                  },
              signal: controller.signal,
            })
            .catch(() => null)
        : Promise.resolve(null)

      const [response, setsResponse] = await Promise.all([cardPromise, setsPromise])

      if (shouldSearchCards && response) {
        const data = response.data?.data || {}
        const resultData = (data.cards || data.products || data.results || []) as any[]
        const normalized = Array.isArray(resultData) ? [...resultData] : []
        const fuzzy = Boolean(data.fuzzy)

        // Fuzzy results arrive pre-ranked by similarity; the relevance scorer
        // would zero out the typo'd tokens and scramble them, so keep server order.
        if (!fuzzy) {
          const scoreResult = (item: any): number =>
            scoreSearchRelevance(
              normalizedQuery,
              item?.name || item?.cardName || '',
              Boolean(item?.imageUrl),
              normalizedSetNumber,
              item?.setNumber || item?.card?.setNumber,
              item?.setCode || item?.set?.setCode || item?.card?.set?.setCode,
            )

          normalized.sort((a: any, b: any) => {
            const aScore = scoreResult(a)
            const bScore = scoreResult(b)
            if (aScore !== bScore) return bScore - aScore
            const aHasImage = Boolean(a?.imageUrl)
            const bHasImage = Boolean(b?.imageUrl)
            if (aHasImage !== bHasImage) return aHasImage ? -1 : 1
            const aName = String(a?.name || a?.cardName || '')
            const bName = String(b?.name || b?.cardName || '')
            return aName.localeCompare(bName, undefined, { sensitivity: 'base' })
          })
        }

        setResults(normalized)
        setIsFuzzy(fuzzy)
        setTotal(
          typeof data.total === 'number'
            ? data.total
            : (Array.isArray(resultData) ? resultData.length : 0)
        )
      } else {
        setResults([])
        setIsFuzzy(false)
        setTotal(0)
      }

      // Set search results
      if (setsResponse?.data?.data?.sets) {
        setSetSearchResults(setsResponse.data.data.sets)
      } else {
        setSetSearchResults([])
      }
      shownSearchKeyRef.current = searchKey
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') {
        return
      }
      console.error('Card search error:', err)
      setResults([])
      setTotal(0)
      shownSearchKeyRef.current = null
    } finally {
      setLoading(false)
      if (searchRequestController.current === controller) {
        searchRequestController.current = null
        inFlightSearchKeyRef.current = null
      }
    }
  }, [selectedGameId, selectedSetId, selectedSetTypes, priceMin, priceMax, releaseDateMin, releaseDateMax, searchType, searchBy, allowSetResults, setNumberQuery])

  const openAllResults = () => {
    const normalizedQuery = normalizeSearchText(query)
    const normalizedSetNumber = normalizeSearchText(setNumberQuery)
    if (!canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH) && !normalizedSetNumber) return
    const sp = new URLSearchParams()
    sp.set('mode', searchType)
    sp.set('q', normalizedQuery)
    if (normalizedSetNumber) sp.set('number', normalizedSetNumber)
    if (selectedGameId) sp.set('gameId', selectedGameId)
    if (selectedSetId) sp.set('setId', selectedSetId)
    if (selectedSetTypes.length > 0) sp.set('setTypes', selectedSetTypes.join(','))
    if (priceMin) sp.set('priceMin', priceMin)
    if (priceMax) sp.set('priceMax', priceMax)
    if (releaseDateMin) sp.set('releaseDateMin', releaseDateMin)
    if (releaseDateMax) sp.set('releaseDateMax', releaseDateMax)
    if (searchType === 'catalog' && allowSetResults) {
      sp.set('searchBy', searchBy)
    }
    if (resultsSource === 'trade') sp.set('source', 'trade')
    if (resultsTradeId) sp.set('tradeId', resultsTradeId)
    if (resultsTradeSide) sp.set('tradeSide', resultsTradeSide)
    navigate(`/search/cards?${sp.toString()}`)
    setShowResults(false)
  }

  const openAllSetResults = () => {
    const normalizedQuery = normalizeSearchText(query)
    if (!canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH)) return
    const sp = new URLSearchParams()
    sp.set('mode', 'catalog')
    sp.set('q', normalizedQuery)
    sp.set('searchBy', 'sets')
    if (selectedGameId) sp.set('gameId', selectedGameId)
    if (selectedSetTypes.length > 0) sp.set('setTypes', selectedSetTypes.join(','))
    navigate(`/search/cards?${sp.toString()}`)
    setShowResults(false)
  }

  const handleInputChange = (value: string) => {
    setQuery(value)
    setShowResults(true)
  }

  const handleSetNumberChange = (value: string) => {
    setSetNumberQuery(value)
    setShowResults(true)
  }

  const handleInputFocus = () => {
    setShowResults(true)
    const normalizedQuery = normalizeSearchText(query)
    if (searchType === 'inventory') {
      void searchCards(normalizedQuery, setNumberQuery, { skipIfCurrent: true })
      return
    }
    if (canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH) || normalizeSearchText(setNumberQuery)) {
      void searchCards(normalizedQuery, setNumberQuery, { skipIfCurrent: true })
    }
  }

  useEffect(() => {
    if (!debouncedQueryFiredRef.current) {
      debouncedQueryFiredRef.current = true
      return
    }
    void searchCards(debouncedQuery, debouncedSetNumberQuery)
  }, [debouncedQuery, debouncedSetNumberQuery])

  // Re-search when filters change (game / set / searchType), using the
  // current query. This does NOT fire on keystroke — that path is handled
  // by the debounced handleInputChange above.
  const prevFiltersRef = useRef({
    searchType,
    selectedGameId,
    selectedSetId,
    selectedSetTypes: selectedSetTypes.join(','),
    searchBy,
  })
  useEffect(() => {
    const prev = prevFiltersRef.current
    const filtersChanged =
      prev.searchType !== searchType ||
      prev.selectedGameId !== selectedGameId ||
      prev.selectedSetId !== selectedSetId ||
      prev.selectedSetTypes !== selectedSetTypes.join(',') ||
      prev.searchBy !== searchBy
    prevFiltersRef.current = {
      searchType,
      selectedGameId,
      selectedSetId,
      selectedSetTypes: selectedSetTypes.join(','),
      searchBy,
    }

    if (!filtersChanged) return // skip on mount & query-only changes

    const normalizedQuery = normalizeSearchText(query)
    if (searchType === 'inventory') {
      searchCards(normalizedQuery)
    } else if (canSearchQuery(normalizedQuery, MIN_QUERY_LENGTH)) {
      searchCards(normalizedQuery)
    }
  }, [searchType, selectedGameId, selectedSetId, selectedSetTypes, searchBy]) // intentionally omit query & searchCards

  // Auto-load results for inventory search on mount
  useEffect(() => {
    if (searchType === 'inventory') {
      searchCards(normalizeSearchText(query))
    }
  }, []) // run once on mount

  const handleSelectCard = (card: CardSearchResult | InventorySearchResult) => {
    onSelectCard(card)
    if (clearQueryOnSelect) {
      setQuery('')
    }
    setResults([])
    // The results just went, so this search no longer answers anything on screen. Left set,
    // re-focusing the box (POS keeps the query after a pick) skipped as "already shown" and
    // opened an empty dropdown.
    shownSearchKeyRef.current = null
    setShowResults(false)
  }

  const handleClear = () => {
    setQuery('')
    setSetNumberQuery('')
    setResults([])
    setTotal(0)
    shownSearchKeyRef.current = null
    inputRef.current?.focus()
  }

  const handleGameChange = (gameId: string) => {
    setSelectedGameId(gameId)
    setSelectedSetId('') // Reset set when game changes
  }

  const handleSetChange = (setId: string) => {
    setSelectedSetId(setId)
  }

  const handleToggleSetType = (setType: SetType) => {
    setSelectedSetTypes((prev) => {
      if (prev.includes(setType)) {
        return prev.filter((value) => value !== setType)
      }
      return [...prev, setType]
    })
  }

  const handleClearFilters = () => {
    setSelectedGameId('')
    setSelectedSetId('')
    setSelectedSetTypes([])
    setPriceMin('')
    setPriceMax('')
    setReleaseDateMin('')
    setReleaseDateMax('')
    setSearchBy(initialSearchBy)
    setSets([])
  }

  const openSetReportModal = (event: React.MouseEvent, setResult: SetSearchResult) => {
    event.preventDefault()
    event.stopPropagation()
    setReportSetContext({
      setId: setResult.id,
      setName: setResult.setName,
      setCode: setResult.setCode,
      gameName: setResult.gameName,
      setType: setResult.setType,
      releaseDate: setResult.releaseDate,
      actualTotal: setResult.cardCount,
      printedTotal: setResult.printedTotal,
      source: 'global_search_set_result',
    })
    setReportSetModalOpen(true)
  }

  const renderCard = (card: CardSearchResult | InventorySearchResult) => {
    const isInventory = 'quantity' in card
    const displayName = isInventory ? card.cardName : card.name
    const gameAccentClass = styles[getGameAccentClassName(card.gameName)]
    const displayPrinting = isInventory
      ? normalizePrintingLabel(card.printing)
      : normalizePrintingLabel((card as CardSearchResult).printing)
    const inventoryRow = isInventory ? (card as InventorySearchResult) : null
    const priceUnavailable = typeof card.priceCents === 'number' ? card.priceCents <= 0 : true
    // A stocked row shows its listed price in the store's currency (formatPrice leaves it untouched
    // when that is the display currency), so search, scan and the inventory screen agree.
    const displayPrice = inventoryRow?.priceUnavailable
      ? 'Price unavailable'
      : priceUnavailable
        ? (isInventory ? 'No price set' : 'Price unavailable')
        : inventoryRow && typeof inventoryRow.storePriceCents === 'number' && isSupportedCurrency(inventoryRow.storeCurrency)
          ? formatPrice(inventoryRow.storePriceCents, inventoryRow.storeCurrency.toUpperCase() as Currency)
          : formatPrice(card.priceCents)
    const variantCount = !isInventory && Array.isArray((card as CardSearchResult).variants)
      ? (card as CardSearchResult).variants.length
      : 0
    const thumbnailUrl = toLowResImageUrl(card.imageUrl)
    const hasVariantMeta = !isInventory && (
      Boolean((card as CardSearchResult).printing) ||
      Boolean((card as CardSearchResult).condition) ||
      variantCount > 0
    )
    const variantSummaryCount = variantCount > 0 ? variantCount : (hasVariantMeta ? 1 : 0)

    // Compute interactive printing / condition data for catalog cards
    const catalogCard = !isInventory ? (card as CardSearchResult) : null
    const distinctPrintings = catalogCard && variantCount > 0
      ? [...new Set(catalogCard.variants.map(v => v.printing).filter(Boolean) as string[])]
      : []
    const currentPrinting = distinctPrintings.length > 0
      ? (selectedPrintingByCard[card.id] || distinctPrintings[0])
      : null
    const conditionsForPrinting = currentPrinting && catalogCard
      ? [...new Set(catalogCard.variants
          .filter(v => v.printing === currentPrinting && v.condition)
          .map(v => v.condition as string))]
      : []

    return (
      <div
        key={`${isInventory ? 'inventory' : 'catalog'}-${card.id}`}
        className={`${styles.resultItem} ${isInventory ? styles.inventoryResult : ''}`}
        onClick={() => handleSelectCard(card)}
      >
        <div className={styles.resultImage}>
          {card.imageUrl ? (
            <button
              type="button"
              className={styles.resultImageZoom}
              onClick={(e) => {
                e.stopPropagation()
                setZoomedImageUrl(card.imageUrl)
                setZoomedImageAlt(displayName)
              }}
              aria-label={`Zoom image for ${displayName}`}
            >
              <img
                src={thumbnailUrl || card.imageUrl}
                alt={displayName}
                onError={(event) => {
                  if (card.imageUrl && event.currentTarget.src !== card.imageUrl) {
                    event.currentTarget.src = card.imageUrl
                  }
                }}
              />
            </button>
          ) : (
            <div className={styles.noImage}>No Image</div>
          )}
        </div>
        <div className={styles.resultInfo}>
          <div className={styles.resultName}><CardName name={displayName} /></div>
          <div className={styles.resultMeta}>
            {hasCardNumber(card.setNumber) && <span className={styles.setNumber}>#{card.setNumber}</span>}
            <span className={styles.setName}>{card.setName}</span>
            {formatSetCode((card as CardSearchResult).setCode) && (
              <span
                className={`${styles.setResultCode} ${gameAccentClass}`}
                style={getSetAccentStyle((card as CardSearchResult).setCode)}
              >
                {formatSetCode((card as CardSearchResult).setCode)}
              </span>
            )}
            {card.rarity && (
              <span className={styles.rarity}>{card.rarity}</span>
            )}
          </div>
          <div className={`${styles.resultGame} ${gameAccentClass}`}>
            {card.gameName}
          </div>
          {isInventory ? (
            <div className={styles.inventoryDetails}>
              <div className={styles.inventoryVariantBadges}>
                <span className={`${styles.variantPill} ${styles[getPrintingClassName(card.printing)]}`}>
                  {displayPrinting}
                </span>
                <span className={`${styles.variantPill} ${styles[getConditionClassName(card.condition)]}`}>
                  {card.condition}
                </span>
              </div>
              <span className={styles.inventoryQtyInline}>Qty {card.quantity}</span>
            </div>
          ) : catalogVariantPreview === 'count' && variantSummaryCount > 0 ? (
            <div className={styles.variantsPreview}>
              <span className={styles.variantBadge}>
                {variantSummaryCount} variant{variantSummaryCount !== 1 ? 's' : ''}
              </span>
            </div>
          ) : variantCount > 0 ? (
            <div className={styles.variantSelector}>
              <div className={styles.printingPillRow}>
                {distinctPrintings.map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`${styles.printingPill} ${styles[getPrintingClassName(p)]}${currentPrinting === p ? ` ${styles.printingPillActive}` : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedPrintingByCard(prev => ({ ...prev, [card.id]: p }))
                    }}
                  >
                    {normalizePrintingLabel(p)}
                  </button>
                ))}
              </div>
              {conditionsForPrinting.length > 0 && (
                <div className={styles.conditionChipRow}>
                  {conditionsForPrinting.map(c => (
                    <span key={c} className={`${styles.conditionChip} ${styles[getConditionClassName(c)]}`}>
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : hasVariantMeta ? (
            <div className={styles.variantsPreview}>
              {(card as CardSearchResult).printing && (
                <span>{displayPrinting}</span>
              )}
              {(card as CardSearchResult).condition && (
                <span>{(card as CardSearchResult).condition}</span>
              )}
            </div>
          ) : null}
        </div>
        <div className={styles.resultPrice}>
          <div className={styles.priceRow}>
            <span>{displayPrice}</span>
            {priceUnavailable && (
              <span
                className={styles.priceWarning}
                title="Price is currently unavailable ($0). Please update before finalising."
              >
                <AlertTriangle size={14} />
                $0
              </span>
            )}
          </div>
          {isInventory && (
            <span className={styles.qtyBadge}>Max {card.quantity}</span>
          )}
          {!isInventory && (card as CardSearchResult).inventoryVariantCount != null &&
            (card as CardSearchResult).inventoryVariantCount! > 0 && (
            <span className={styles.qtyBadge} title="This card is in your inventory">
              In stock{(card as CardSearchResult).inventoryVariantCount! > 1
                ? ` (${(card as CardSearchResult).inventoryVariantCount} variants)`
                : ''}
            </span>
          )}
          {!isInventory && (
            <button
              type="button"
              className={styles.reportQuickButton}
              title="Report issue with this card"
              onClick={(event) => {
                event.stopPropagation()
                setReportContext({
                  cardName: (card as CardSearchResult).name,
                  cardId: card.id,
                  setName: card.setName,
                  setCode: (card as CardSearchResult).setCode,
                  setNumber: card.setNumber,
                  gameName: card.gameName,
                  rarity: card.rarity,
                  priceCents: card.priceCents,
                  source: 'search',
                })
                setReportModalOpen(true)
              }}
            >
              <Flag size={12} />
              Report
            </button>
          )}
        </div>
      </div>
    )
  }

  const filterFields: FilterField[] = [
    ...(showGameFilter
      ? [{
          id: 'game',
          label: 'Game',
          kind: 'single' as const,
          options: [{ value: '', label: 'All games' }, ...filteredGames.map((game) => ({ value: game.id, label: game.name }))],
          value: selectedGameId,
          onChange: handleGameChange,
        }]
      : []),
    ...(showSetFilter
      ? [{
          id: 'set',
          label: 'Set',
          kind: 'single' as const,
          unavailableHint: !selectedGameId ? 'Choose a game first.' : loadingSets ? 'Loading sets…' : undefined,
          options: [
            { value: '', label: 'All sets' },
            ...filteredSets.map((set) => ({
              value: set.id,
              label: set.releaseDate
                ? `${set.name} (${new Date(set.releaseDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })})`
                : set.name,
            })),
          ],
          value: selectedSetId,
          onChange: handleSetChange,
        }]
      : []),
    ...(showResultTypeFilter && searchType === 'catalog'
      ? [{
          id: 'show',
          label: 'Show',
          kind: 'single' as const,
          options: [
            { value: 'cards', label: 'Cards' },
            { value: 'sets', label: 'Sets' },
            { value: 'both', label: 'Cards & sets' },
          ],
          value: searchBy,
          defaultValue: initialSearchBy,
          onChange: (value: string) => setSearchBy(value as 'cards' | 'sets' | 'both'),
        }]
      : []),
    ...(showSetTypeOptions
      ? [{
          id: 'setType',
          label: 'Set type',
          kind: 'multi' as const,
          options: SET_TYPE_OPTIONS,
          selected: selectedSetTypes,
          onToggle: (value: string) => handleToggleSetType(value as SetType),
        }]
      : []),
    {
      id: 'price',
      label: 'Price',
      kind: 'range' as const,
      inputType: 'number' as const,
      prefix: '$',
      min: priceMin,
      max: priceMax,
      onMinChange: setPriceMin,
      onMaxChange: setPriceMax,
    },
    {
      id: 'released',
      label: 'Released',
      kind: 'range' as const,
      inputType: 'date' as const,
      min: releaseDateMin,
      max: releaseDateMax,
      onMinChange: setReleaseDateMin,
      onMaxChange: setReleaseDateMax,
    },
  ]

  const containerClasses = `${styles.searchContainer} ${className} ${variant === 'setsLike' ? styles.setsLike : ''} ${inline ? styles.inlinePanel : ''}`

  // The name box's × clears both boxes; this one is only for a search on the set number alone.
  const setNumberOnlyClear = !query && setNumberQuery && !loading ? (
    <button
      type="button"
      onClick={handleClear}
      className={styles.clearInlineButton}
      aria-label="Clear search"
    >
      <X size={16} />
    </button>
  ) : null

  return (
    <div ref={containerRef} className={containerClasses}>
      {variant === 'setsLike' ? (
        <div className={styles.setsLikeRow}>
          <SearchInput
            ref={inputRef}
            className={styles.setsLikeBox}
            value={query}
            onValueChange={handleInputChange}
            onClear={handleClear}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            ariaLabel={placeholder}
            loading={loading}
          />
          {showSetNumberInput && (
            <div className={styles.setsLikeSetNumberBox}>
              <input
                type="text"
                value={setNumberQuery}
                onChange={(e) => handleSetNumberChange(e.target.value)}
                onFocus={handleInputFocus}
                placeholder={setNumberPlaceholder}
                className={styles.setsLikeSetNumberInput}
                aria-label="Search by set number or set code"
                title="Collector number (0044, 014/055) or set code (SWSH02, FB02-23)"
              />
            </div>
          )}
          {setNumberOnlyClear}
        </div>
      ) : (
        <SearchInput
          ref={inputRef}
          value={query}
          onValueChange={handleInputChange}
          onClear={handleClear}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          ariaLabel={placeholder}
          loading={loading}
          trailing={(
            <>
              {showSetNumberInput && (
                <input
                  type="text"
                  value={setNumberQuery}
                  onChange={(e) => handleSetNumberChange(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder={setNumberPlaceholder}
                  className={styles.setNumberInput}
                  aria-label="Search by set number or set code"
                  title="Collector number (0044, 014/055) or set code (SWSH02, FB02-23)"
                />
              )}
              {setNumberOnlyClear}
            </>
          )}
        />
      )}

      {/* Filters and results: drop down from the box, or fill the parent when `inline`. */}
      {(inline || showResults) && (
        <div className={styles.searchPanel}>
          {(panelHeader || hasFilters) && (
            <div className={styles.searchPanelFilters}>
              {panelHeader}
              {hasFilters && <FilterBar fields={filterFields} onClearAll={handleClearFilters} />}
            </div>
          )}

      {(hasSearchInput || results.length > 0 || (allowSetResults && setSearchResults.length > 0)) && (
        <div className={styles.resultsDropdown}>
          {loading ? (
            <div className={styles.resultsLoading}>
              <Loader2 className={styles.spinner} size={20} />
              <span>Searching...</span>
            </div>
          ) : (() => {
            const showSetSection = allowSetResults && setSearchResults.length > 0 && searchBy !== 'cards'
            const showCardSection = results.length > 0 && searchBy !== 'sets'
            const nothingToShow = !showSetSection && !showCardSection
            return nothingToShow ? (
              <div className={styles.noResults}>
                <div>
                  <div>
                    {searchType === 'catalog' && !hasSearchInput
                      ? 'Type at least 2 characters to search'
                      : searchBy === 'sets'
                        ? 'No sets found matching your search'
                        : searchBy === 'cards'
                          ? 'No cards found matching your search'
                          : selectedSetTypes.length > 0 && allowSetResults
                            ? 'No cards or sets found matching your search'
                            : 'No cards found matching your search'
                    }
                  </div>
                  {onManualAdd && (
                    <button
                      type="button"
                      className={styles.resultsMoreButton}
                      onClick={() => onManualAdd(query)}
                      style={{ marginTop: '0.75rem' }}
                    >
                      {manualAddLabel}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Set search results */}
                {showSetSection && (
                  <div className={styles.setResultsSection}>
                    <div className={styles.setResultsHeader}>
                      <Package size={14} />
                      <span>Sets</span>
                    </div>
                    {setSearchResults.map(setResult => (
                      <div
                        key={`set-${setResult.id}`}
                        className={styles.setResultItem}
                        onClick={() => {
                          const sp = new URLSearchParams()
                          sp.set('mode', 'catalog')
                          sp.set('q', '')
                          if (setResult.gameId) sp.set('gameId', setResult.gameId)
                          sp.set('setId', setResult.id)
                          sp.set('sort', 'set_number_asc')
                          navigate(`/search/cards?${sp.toString()}`)
                          setShowResults(false)
                                          }}
                      >
                        <div className={styles.setResultIcon}>
                          <SetImage
                            logoUrl={setResult.logoUrl}
                            symbolUrl={setResult.symbolUrl}
                            coverImageUrl={setResult.coverImageUrl}
                            setCode={setResult.setCode}
                            setName={setResult.setName}
                            size="sm"
                          />
                        </div>
                        <div className={styles.setResultInfo}>
                          <div className={styles.setResultName}>
                            {setResult.setName}
                            {formatSetCode(setResult.setCode) && (
                              <span
                                className={`${styles.setResultCode} ${styles[getGameAccentClassName(setResult.gameName)]}`}
                                style={getSetAccentStyle(setResult.setCode)}
                              >
                                {formatSetCode(setResult.setCode)}
                              </span>
                            )}
                          </div>
                          <div className={styles.setResultMeta}>
                            <span className={`${styles.setResultGame} ${styles[getGameAccentClassName(setResult.gameName)]}`}>{setResult.gameName}{setResult.language && setResult.language !== 'en' ? ` (${setResult.language.toUpperCase()})` : ''}</span>
                            <span>·</span>
                            <span>{setResult.cardCount} card{setResult.cardCount !== 1 ? 's' : ''}</span>
                            {setResult.releaseDate && (
                              <>
                                <span>·</span>
                                <span>{new Date(setResult.releaseDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={styles.setResultActions}>
                          <button
                            type="button"
                            className={styles.reportQuickButton}
                            title="Report issue with this set"
                            onClick={(event) => openSetReportModal(event, setResult)}
                          >
                            <Flag size={14} />
                            Report
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className={styles.setResultsFooter}>
                      <button type="button" className={styles.resultsMoreButton} onClick={openAllSetResults}>
                        View all set results
                      </button>
                    </div>
                  </div>
                )}

                {showCardSection && (
                  <>
                    <div className={styles.resultsHeader}>
                      <div className={styles.resultsHeaderTitle}>
                        <span>
                          {isFuzzy
                            ? `No exact matches — ${total} closest match${total !== 1 ? 'es' : ''}`
                            : `Found ${total} card${total !== 1 ? 's' : ''}`}
                        </span>
                        {(hasSearchInput || results.length > 0 || setSearchResults.length > 0) && (
                          <button type="button" className={styles.viewAllLink} onClick={openAllResults}>
                            View all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className={styles.resultsList}>
                      {results.map(card => renderCard(card))}
                    </div>
                  </>
                )}

                {(hasSearchInput || results.length > 0 || setSearchResults.length > 0) && (
                  <div className={styles.resultsMore}>
                    <button type="button" className={styles.resultsMoreButton} onClick={openAllResults}>
                      View all results
                    </button>
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}
        </div>
      )}

      {reportModalOpen && reportContext && (
        <ReportIssueModal
          isOpen={reportModalOpen}
          onClose={() => {
            setReportModalOpen(false)
            setReportContext(null)
          }}
          context={reportContext}
        />
      )}

      {reportSetModalOpen && reportSetContext && (
        <ReportSetIssueModal
          isOpen={reportSetModalOpen}
          onClose={() => {
            setReportSetModalOpen(false)
            setReportSetContext(null)
          }}
          context={reportSetContext}
        />
      )}

      <CardImageModal
        imageUrl={zoomedImageUrl}
        alt={zoomedImageAlt}
        isOpen={zoomedImageUrl !== null}
        onClose={() => {
          setZoomedImageUrl(null)
          setShowResults(true)
        }}
      />
    </div>
  )
}

export default UnifiedCardSearch
